import { useState } from 'react';
import { Link as RouterLink } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useQuery } from '@tanstack/react-query';

import {
  Button,
  Link,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from '@mui/material';

import { apiClient } from '@/shared/api';
import { ErrorState, LoadingState } from '@/shared/ui';
import { formatQuantity } from '@/shared/lib/quantityFormat';

import { listStoreOverrides } from '../../api/channelStockApi';
import { channelStockKeys } from '../../api/hooks';
import { searchLocations } from '../../api/locationsApi';
import type { LocationGroup, StoreScope } from '../../model/types';

interface StockRow {
  locationId: string;
  onHandQty: number;
  reservedQty: number;
  availableQty: number;
  uom?: string;
}

/**
 * Derivation drill-down (design-stock-channels-page §8.5): every per-store number expands into
 * locations → Σ group → buffer → override → clamp. v1 composes CLIENT-SIDE from the existing
 * per-product stock read + the loaded scope + the store's overrides (spec Q2's "start composed"
 * recommendation) — the formula mirrors inventory's AvailabilityCalculator; if the composed number
 * ever diverges from the till, the scoped batch API is the arbiter (Q2 follow-up: a BFF derivation
 * endpoint).
 */
export function StoreDerivationPanel({ scope, group }: { scope: StoreScope; group?: LocationGroup }) {
  const { t } = useTranslation('channelStock');
  const [productId, setProductId] = useState('');
  const [requestedId, setRequestedId] = useState<string | null>(null);

  const locationsQuery = useQuery({ queryKey: channelStockKeys.locations, queryFn: () => searchLocations() });
  const locationName = (locationId: string) => {
    const location = (locationsQuery.data ?? []).find((entry) => entry.id === locationId);
    return location ? `${location.name} (${location.code})` : `${locationId.slice(0, 8)}…`;
  };

  const derivation = useQuery({
    queryKey: channelStockKeys.derivation(scope.storeCode, requestedId ?? ''),
    enabled: Boolean(requestedId),
    queryFn: async () => {
      const stock = (await apiClient.get<{ items: StockRow[] }>(`/api/v1/inventory/stock/${requestedId}`)).data;
      const overrides = await listStoreOverrides(scope.storeCode);
      return { stock: stock.items ?? [], override: overrides.find((o) => o.productId === requestedId) ?? null };
    },
  });

  const groupLocationIds = new Set(group?.locationIds ?? []);
  const rows = (derivation.data?.stock ?? []).filter((row) => groupLocationIds.has(row.locationId));
  const pool = rows.reduce((sum, row) => sum + row.availableQty, 0);

  // Mirrors AvailabilityCalculator.allocated() exactly: BOTH type and value must be present,
  // else the server falls back to the full pool (never a guessed FIXED_QTY quota).
  const quota =
    scope.allocationMode === 'ALLOCATED' && scope.allocationValue != null && scope.allocationType != null
      ? scope.allocationType === 'PERCENT'
        ? (pool * scope.allocationValue) / 100
        : scope.allocationValue
      : null;
  const afterMode = quota != null ? Math.min(quota, pool) : pool;
  const bufferCut =
    scope.displayBuffer != null
      ? scope.bufferType === 'PERCENT'
        ? (afterMode * scope.displayBuffer) / 100
        : scope.displayBuffer
      : 0;
  const afterBuffer = afterMode - bufferCut;
  const override = derivation.data?.override ?? null;
  const activeOverride =
    override && !override.expiredApplied && (!override.expiresAt || new Date(override.expiresAt) > new Date())
      ? override
      : null;
  const afterOverride = activeOverride
    ? activeOverride.mode === 'BLOCK'
      ? 0
      : activeOverride.mode === 'PIN'
        ? (activeOverride.value ?? 0)
        : afterBuffer + (activeOverride.value ?? 0)
    : afterBuffer;
  const finalValue = Math.max(0, afterOverride);
  const uom = rows[0]?.uom;

  return (
    <Stack spacing={2} sx={{ py: 2 }}>
      <Typography variant="subtitle2">{t('stores.derivation.title')}</Typography>
      <Stack direction="row" spacing={1}>
        <TextField
          size="small"
          label={t('stores.derivation.productIdLabel')}
          value={productId}
          onChange={(event) => setProductId(event.target.value)}
          sx={{ minWidth: 360 }}
          slotProps={{ htmlInput: { style: { fontFamily: '"JetBrains Mono", monospace' } } }}
        />
        <Button variant="outlined" disabled={!productId.trim()} onClick={() => setRequestedId(productId.trim())}>
          {t('stores.derivation.show')}
        </Button>
      </Stack>

      {!requestedId ? (
        <Typography variant="body2" color="text.secondary">{t('stores.derivation.idleHint')}</Typography>
      ) : derivation.isLoading ? (
        <LoadingState fullHeight={false} />
      ) : derivation.isError ? (
        <ErrorState onRetry={() => void derivation.refetch()} />
      ) : (
        <Table size="small" sx={{ maxWidth: 720 }}>
          <TableHead>
            <TableRow>
              <TableCell>{t('stores.derivation.location')}</TableCell>
              <TableCell align="right">{t('stores.derivation.onHand')}</TableCell>
              <TableCell align="right">{t('stores.derivation.reserved')}</TableCell>
              <TableCell align="right">{t('stores.derivation.available')}</TableCell>
              <TableCell />
            </TableRow>
          </TableHead>
          <TableBody>
            {rows.map((row) => (
              <TableRow key={row.locationId}>
                <TableCell>{locationName(row.locationId)}</TableCell>
                <TableCell align="right">{formatQuantity(row.onHandQty)}</TableCell>
                <TableCell align="right">{formatQuantity(row.reservedQty)}</TableCell>
                <TableCell align="right">{formatQuantity(row.availableQty)}</TableCell>
                <TableCell>
                  <Link
                    component={RouterLink}
                    to={`/inventory?tab=ledger&productId=${requestedId}&locationId=${row.locationId}`}
                  >
                    {t('stores.derivation.ledgerLink')}
                  </Link>
                </TableCell>
              </TableRow>
            ))}
            <TableRow>
              <TableCell sx={{ fontWeight: 600 }}>{t('stores.derivation.pool', { group: group?.name ?? '' })}</TableCell>
              <TableCell colSpan={3} align="right" sx={{ fontWeight: 600 }}>{formatQuantity(pool)}</TableCell>
              <TableCell />
            </TableRow>
            {quota != null ? (
              <TableRow>
                <TableCell>{t('stores.derivation.quota')}</TableCell>
                <TableCell colSpan={3} align="right">{formatQuantity(afterMode)}</TableCell>
                <TableCell />
              </TableRow>
            ) : null}
            <TableRow>
              <TableCell>{t('stores.derivation.buffer')}</TableCell>
              <TableCell colSpan={3} align="right">
                {bufferCut > 0 ? `−${formatQuantity(bufferCut)}` : t('stores.derivation.none')}
              </TableCell>
              <TableCell />
            </TableRow>
            <TableRow>
              <TableCell>{t('stores.derivation.override')}</TableCell>
              <TableCell colSpan={3} align="right">
                {activeOverride
                  ? `${t(`overrides.mode.${activeOverride.mode.toLowerCase()}`)} · ${activeOverride.reason} · ${activeOverride.author}`
                  : t('stores.derivation.none')}
              </TableCell>
              <TableCell />
            </TableRow>
            {afterOverride < 0 ? (
              <TableRow>
                <TableCell colSpan={5}>
                  <Typography variant="caption" color="text.secondary">{t('stores.derivation.clampNote')}</Typography>
                </TableCell>
              </TableRow>
            ) : null}
            <TableRow>
              <TableCell sx={{ fontWeight: 700 }}>{t('stores.derivation.total')}</TableCell>
              <TableCell colSpan={3} align="right" sx={{ fontWeight: 700 }}>
                {formatQuantity(finalValue)} {uom ?? ''}
              </TableCell>
              <TableCell />
            </TableRow>
          </TableBody>
        </Table>
      )}
    </Stack>
  );
}
